import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { sessionService } from "../../application/SessionApplicationService";
import { Session, TriggeredAlert } from "../../domain/Session";
import { AlertTriangle, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router";
import Label from "../../../../shared/ui/form/Label";
import { useAiInsightsGate, LockedState } from "../../../ai_insights";

interface AlertHistoryItem {
    id: string; // session_id + alert_id
    sessionId: string;
    sessionDate: string;
    alertId: string;
    name: string;
    criticality: string;
    context: string;
}

interface ClinicalAlertsHistoryProps {
    patientId: string;
    isCouple?: boolean;
    limit?: number;
    title?: string | null;
    compact?: boolean;
    variant?: 'list' | 'grid';
    refreshKey?: number;
    showMoreLink?: string;
}

export default function ClinicalAlertsHistory({
    patientId,
    isCouple = false,
    limit = 20,
    title,
    compact = false,
    variant = 'list',
    refreshKey = 0,
    showMoreLink
}: ClinicalAlertsHistoryProps) {
    const { t } = useTranslation();
    const { adopted: aiInsightsAdopted, loading: adoptionLoading } = useAiInsightsGate();
    const [alerts, setAlerts] = useState<AlertHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchAlerts() {
            try {
                setLoading(true);
                // Fetch more sessions to have a better chance of finding alerts
                // The 'limit' prop determines how many ALERTS to show, not how many sessions to fetch
                const response = isCouple
                    ? await sessionService.listSessionsByCouplePatient(patientId, { limit: 100 })
                    : await sessionService.listSessionsByPatient(patientId, { limit: 100 });
                const sessions = response.sessions;

                const history: AlertHistoryItem[] = [];

                sessions.forEach((session: Session) => {
                    // Check for alerts in root (new API) or analysis (old API/detail view)
                    const alerts = session.triggered_alerts || session.analysis?.triggered_alerts || [];

                    if (alerts.length > 0) {
                        alerts.forEach((alert: TriggeredAlert) => {
                            history.push({
                                id: `${session.id}-${alert.alert_id}`,
                                sessionId: session.id,
                                sessionDate: session.date,
                                alertId: alert.alert_id,
                                name: alert.name,
                                criticality: alert.criticality,
                                context: alert.context
                            });
                        });
                    }
                });

                // If simple limit on sessions didn't give enough alerts, we might need more, but for now we trust the limit
                // We store the full history to decide if we show "Show more"
                setAlerts(history);
            } catch (error) {
                console.error("Failed to fetch alerts history", error);
            } finally {
                setLoading(false);
            }
        }

        if (patientId) {
            fetchAlerts();
        }
    }, [patientId, limit, refreshKey, isCouple]);

    if (loading || adoptionLoading) return <div className="animate-pulse h-20 bg-gray-50 rounded-lg mt-4"></div>;

    // Alert history is part of the AI Insights tool.
    if (!aiInsightsAdopted) {
        return (
            <div className={compact ? "mt-2" : "mt-4"}>
                <LockedState title={t("aiInsights.locked.clinicalAlerts")} compact={compact} />
            </div>
        );
    }

    if (alerts.length === 0) {
        return (
            <div className={compact ? "mt-2" : "mt-4"}>
                {title !== null && (
                    <Label>{title || t("patient.alertsHistory", "Clinical Alerts History")}</Label>
                )}
                <div className="p-4 bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-gray-800 rounded-lg text-center">
                    <p className="text-sm text-gray-500">{t("patient.noAlertsFound")}</p>
                </div>
            </div>
        );
    }

    const getAlertStyle = (criticality: string) => {
        const lower = criticality.toLowerCase();
        if (lower.includes("low") || lower.includes("baja")) {
            return {
                normalized: "low",
                border: "border-accent/20",
                bg: "bg-accent-tint",
                text: "text-accent-on-tint",
                badge: "bg-accent-tint text-accent-on-tint text-accent-text",
                iconText: "text-accent-text"
            };
        }
        if (lower.includes("medium") || lower.includes("media") || lower.includes("yellow")) {
            return {
                normalized: "medium",
                border: "border-yellow-100 dark:border-yellow-900/30",
                bg: "bg-yellow-50 dark:bg-yellow-900/10",
                text: "text-yellow-800 dark:text-yellow-200",
                badge: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
                iconText: "text-yellow-500"
            };
        }
        return {
            normalized: "critical",
            border: "border-red-100 dark:border-red-900/30",
            bg: "bg-red-50 dark:bg-red-900/10",
            text: "text-red-800 dark:text-red-200",
            badge: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
            iconText: "text-red-500"
        };
    };

    const containerClass = variant === 'grid'
        ? "grid grid-cols-1 md:grid-cols-2 gap-4"
        : `space-y-3 ${compact ? "mt-1" : "mt-2"}`;

    return (
        <div className={compact ? "mt-2" : "mt-4"}>
            {title !== null && (
                <Label>{title || t("patient.alertsHistory", "Clinical Alerts History")}</Label>
            )}
            <div className={containerClass}>
                {alerts.slice(0, limit).map((item) => {
                    const style = getAlertStyle(item.criticality);
                    return (
                        <div key={item.id} className={`flex flex-col gap-2 p-3 rounded-lg border shadow-sm ${style.bg} ${style.border} ${variant === 'grid' ? 'h-full' : ''}`}>
                            <div className="flex items-start gap-3">
                                <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${style.iconText}`} />
                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-start mb-1">
                                        <h4 className={`text-sm font-semibold truncate pr-2 ${style.text}`}>{item.name}</h4>
                                        <Link
                                            to={`/session/${item.sessionId}`}
                                            className={`flex items-center text-xs gap-1 hover:underline whitespace-nowrap ${style.iconText}`}
                                        >
                                            <Clock className="w-3 h-3" />
                                            {new Date(item.sessionDate).toLocaleDateString()}
                                            <ArrowRight className="w-3 h-3 ml-0.5" />
                                        </Link>
                                    </div>
                                    <p className={`text-xs leading-relaxed opacity-90 ${style.text}`}>
                                        {item.context}
                                    </p>
                                    <div className="mt-2">
                                        <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded border uppercase tracking-wider ${style.badge} ${style.border}`}>
                                            {t(`clinicalAlerts.modal.criticality.${style.normalized}`)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {showMoreLink && alerts.length > limit && (
                <div className="mt-3 px-1">
                    <Link
                        to={showMoreLink}
                        className="text-xs text-accent-text hover:text-accent-hover hover:underline font-medium"
                    >
                        {t("common.showMore", "Show more")}...
                    </Link>
                </div>
            )}
        </div>
    );
}
